/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging";
import { StorageAdapter } from "../storage/StorageAdapter";
import {
  STORAGE_KEY_ACCESS_TOKEN_WITH_EXPIRY,
  STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX
} from "../storage/storageConfig";
import { ACCOUNT_CONTEXT_ID } from "./tokenConstants";

/**
 * Storage TTL for token records: never expire. `StorageAdapter` computes
 * `validTill = now + ttl`, so `Infinity` keeps `get` from ever reporting the
 * record as elapsed, and the default `evictTill` (also `Infinity`) keeps the
 * startup cleanup from sweeping it.
 */
const TOKEN_STORAGE_TTL_MS: number = Number.POSITIVE_INFINITY;

/**
 * One persisted domain token, scoped to an authorization context.
 *
 * `profileId` is `null` for the account-level token and a profile ID for a
 * profile-scoped one — the same convention the whole auth package uses.
 */
export interface TStoredToken {
  token: string;
  domain: string;
  /** Expiry as Unix epoch **seconds**, as the domain-token service returns it. */
  expiration: number;
  profileId: string | null;
}

/**
 * Shape of the pre-consolidation account-token record. Read once by
 * {@link TokenStore.readLegacyAccountToken} so devices upgrading from a build
 * that wrote `accessTokenWithExpiry` keep their session, then deleted.
 */
interface TLegacyAccessTokenRecord {
  accessToken: string;
  tokenExpirationTime: Date | number | string;
}

/**
 * The only persistence layer for domain tokens.
 *
 * One record per (bodyId, context) pair, written with an **infinite** storage
 * TTL. A record is removed only when the session must be cleared — sign-out,
 * a failed sign-in, a deleted profile, a device-identity reset — never because
 * the token inside it has expired.
 *
 * Expiry is the token's business, not the store's: {@link TokenManager}
 * compares `expiration` against the 5-minute skew window and refreshes. An
 * expired record is still the proof that this device has a session to renew;
 * letting storage delete it would turn a routine silent refresh into a trip
 * back to the sign-in screen.
 *
 * Replaces the previous split where the account token lived in
 * `accessTokenWithExpiry` (written by `DeviceModel`) *and* in
 * `profileDomainToken:<bodyId>:account`, with different readers for each.
 */
export class TokenStore {
  private static TAG: string = "TokenStore";

  /**
   * Builds the record key for one context. `null`/empty maps to the literal
   * `"account"` suffix, matching what earlier builds already wrote to disk.
   */
  private static buildKey(bodyId: string, profileId: string | null): string {
    const contextId: string =
      profileId !== null && profileId.length > 0
        ? profileId
        : ACCOUNT_CONTEXT_ID;
    return (
      STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX +
      encodeURIComponent(bodyId) +
      ":" +
      encodeURIComponent(contextId)
    );
  }

  /**
   * Reads the stored token for a context, expired or not.
   *
   * @returns The token, or `null` when no record exists.
   */
  public static async get(
    bodyId: string,
    profileId: string | null
  ): Promise<TStoredToken | null> {
    try {
      return await StorageAdapter.getInstance().get<TStoredToken>(
        TokenStore.buildKey(bodyId, profileId)
      );
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "get()", error);
      throw error;
    }
  }

  /**
   * Writes a token with no storage expiry.
   *
   * An already-expired token is written too: the record still marks a session
   * that {@link TokenManager} can refresh.
   */
  public static async set(
    bodyId: string,
    storedToken: TStoredToken
  ): Promise<void> {
    try {
      await StorageAdapter.getInstance().set(
        TokenStore.buildKey(bodyId, storedToken.profileId),
        storedToken,
        TOKEN_STORAGE_TTL_MS
      );
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "set()", error);
      throw error;
    }
  }

  /** Removes the stored token for one context. */
  public static async delete(
    bodyId: string,
    profileId: string | null
  ): Promise<void> {
    try {
      await StorageAdapter.getInstance().delete(
        TokenStore.buildKey(bodyId, profileId)
      );
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "delete()", error);
      throw error;
    }
  }

  /**
   * Removes every persisted token, for every body and every context.
   * Called on sign-out, where leaving any context behind would let the next
   * user inherit a session.
   */
  public static async deleteAll(): Promise<void> {
    try {
      const adapter: StorageAdapter = StorageAdapter.getInstance();
      const keys: string[] = await adapter.keysWithPrefix(
        STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX
      );
      for (const key of keys) {
        await adapter.delete(key);
      }
      await adapter.delete(STORAGE_KEY_ACCESS_TOKEN_WITH_EXPIRY);
      Log.i(TokenStore.TAG, "deleteAll() cleared " + keys.length + " contexts");
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "deleteAll()", error);
      throw error;
    }
  }

  /**
   * Moves every stored token from one body to another.
   *
   * Records are keyed by bodyId, but a first-ever sign-in mints its domain
   * token *before* `provisionDevices` tells the device what its bodyId is — so
   * those records land under the empty-bodyId key. Re-keying them the moment
   * the real bodyId arrives is what lets the next boot find the session under
   * the key it reads.
   *
   * Expired tokens move like any other. A record whose value can no longer be
   * read (checksum-rejected) is dropped rather than carried over: there is
   * nothing usable to move, and leaving it behind under a stale body would only
   * accumulate.
   */
  public static async rekey(
    fromBodyId: string,
    toBodyId: string
  ): Promise<void> {
    if (fromBodyId === toBodyId) {
      return;
    }
    try {
      const adapter: StorageAdapter = StorageAdapter.getInstance();
      // Keys are `<prefix><encoded bodyId>:<encoded context>`, so the trailing
      // separator keeps an empty bodyId from matching every other body.
      const prefix: string =
        STORAGE_KEY_PROFILE_DOMAIN_TOKEN_PREFIX +
        encodeURIComponent(fromBodyId) +
        ":";
      const keys: string[] = await adapter.keysWithPrefix(prefix);
      for (const key of keys) {
        const stored: TStoredToken | null =
          await adapter.get<TStoredToken>(key);
        await adapter.delete(key);
        if (stored !== null && stored !== undefined) {
          await TokenStore.set(toBodyId, stored);
        }
      }
      Log.i(TokenStore.TAG, "rekey() moved " + keys.length + " records");
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "rekey()", error);
      throw error;
    }
  }

  /**
   * Reads the pre-consolidation `accessTokenWithExpiry` record.
   *
   * Only {@link TokenManager.restore} calls this, once per app start, to carry
   * a session across the upgrade to the consolidated store. Returns `null`
   * when the record is absent or malformed. An expired record is still
   * migrated: it marks a session the normal refresh path can renew.
   */
  public static async readLegacyAccountToken(): Promise<TStoredToken | null> {
    try {
      const record: TLegacyAccessTokenRecord | null =
        await StorageAdapter.getInstance().get<TLegacyAccessTokenRecord>(
          STORAGE_KEY_ACCESS_TOKEN_WITH_EXPIRY
        );
      if (record === null || record === undefined) {
        return null;
      }
      if (
        typeof record.accessToken !== "string" ||
        record.accessToken.length === 0
      ) {
        return null;
      }
      // IndexedDB round-trips a Date as a Date, but the record predates this
      // store and older writes may have left a number or an ISO string.
      const expirationMs: number =
        record.tokenExpirationTime instanceof Date
          ? record.tokenExpirationTime.getTime()
          : Number(record.tokenExpirationTime);
      if (Number.isNaN(expirationMs)) {
        return null;
      }
      return {
        token: record.accessToken,
        domain: "",
        expiration: Math.floor(expirationMs / 1000),
        profileId: null
      };
    } catch (error: unknown) {
      // A migration read must never be able to fail a boot.
      Log.e(TokenStore.TAG, "readLegacyAccountToken()", error);
      return null;
    }
  }

  /** Drops the pre-consolidation record once it has been migrated or superseded. */
  public static async deleteLegacyAccountToken(): Promise<void> {
    try {
      await StorageAdapter.getInstance().delete(
        STORAGE_KEY_ACCESS_TOKEN_WITH_EXPIRY
      );
    } catch (error: unknown) {
      Log.e(TokenStore.TAG, "deleteLegacyAccountToken()", error);
    }
  }
}
